/**
 * ProjectForm — create-a-project SlidePanel for ManageClientsPage.
 *
 * Task 114 D3. Create-only in this task; edit is handled by the existing
 * /projects/:projectId/edit flow on ManageProjectsPage (see task body § D3).
 *
 * Pattern mirrors the in-file ClientForm in ManageClientsPage.tsx:
 *   - Panel opens when the parent page matches /clients/:clientId/projects/new.
 *   - Parent page owns the useMatch + hook wiring and passes createProject in.
 *   - onSuccess closes the panel; onError surfaces inline via err.message.
 *   - Mockup mode is not special-cased here — matches createClient's behaviour.
 *
 * The clientId is pinned by the URL (read-only row). There is no client
 * selector, no description/tags/status, no delete (out of scope fences on the
 * task).
 */
import { useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Button } from '@/components/primitives/Button';
import { Checkbox } from '@/components/primitives/Checkbox';
import { TextInput } from '@/components/primitives/TextInput';
import styles from './ProjectForm.module.css';

type CreateProjectMutation = UseMutationResult<
  unknown,
  Error,
  { name: string; clientId: string; slug?: string; billable?: boolean }
>;

interface ProjectFormProps {
  clientId: string;
  clientName: string;
  onClose: () => void;
  createProject: CreateProjectMutation;
}

export function ProjectForm({ clientId, clientName, onClose, createProject }: ProjectFormProps) {
  const [name, setName] = useState('');
  const [billable, setBillable] = useState(true);
  const [error, setError] = useState('');

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !createProject.isPending;

  const handleSave = () => {
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    setError('');

    createProject.mutate(
      { name: trimmedName, clientId, billable },
      {
        onSuccess: onClose,
        onError: (err) => {
          // The backend returns 409 with a message like "A project with this
          // slug already exists for this client" when the auto-slug collides.
          // Re-phrase to talk about the name the user typed — matches the
          // "nobody types a slug here" UX.
          const msg = err.message ?? '';
          if (msg.toLowerCase().includes('already exists')) {
            setError(`A project with this name already exists under ${clientName}.`);
          } else {
            setError(msg || 'Failed to create project');
          }
        },
      },
    );
  };

  return (
    <SlidePanel
      open
      onClose={onClose}
      title={`Add Project to ${clientName}`}
      width="sm"
      footer={
        <div className={styles.panelFooter}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSubmit}>
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </Button>
        </div>
      }
    >
      <div className={styles.panelBody}>
        {error && (
          <div className={styles.formError} role="alert">
            {error}
          </div>
        )}
        <div className={styles.clientRow}>
          <span className={styles.clientLabel}>Client</span>
          <span className={styles.clientValue}>{clientName}</span>
        </div>
        <TextInput
          label="Name"
          placeholder="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Checkbox checked={billable} onChange={setBillable} label="Billable" />
      </div>
    </SlidePanel>
  );
}
